import { apiClient } from "../client";
import { ENDPOINTS } from "../endpoints";

// KX-33 — BYO Activation Fee Schedule (admin CRUD)
// Backend: /api/admin/activation-fees (REST: list/create/get/update/delete).
// Fees are per country_code (ISO-2 or "DEFAULT") + hardware_class. `applies_to`
// is structurally forced to "byo" server-side and must NEVER be sent by the UI.

export type HardwareClass = "entry" | "mid" | "high";

export interface AdminActivationFee {
  _id?: string;
  country_code: string; // ISO-2 or "DEFAULT"
  currency: string; // ISO-4217 alpha-3
  hardware_class: HardwareClass;
  // Integer minor units, returned by the API as a string. Never do arithmetic
  // on this beyond formatting for display.
  fee_minor: string;
  tax_profile_id?: string | null;
  effective_from?: string | null;
  effective_to?: string | null;
  // Echoed back by the API but forced server-side; never sent by the UI.
  applies_to?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface AdminActivationFeeResponse {
  success?: boolean;
  message?: string;
  count?: number;
  data?: AdminActivationFee | AdminActivationFee[];
}

export interface ActivationFeeFilters {
  country_code?: string;
  hardware_class?: HardwareClass | "";
}

/** A row from the admin commercial-config currency/country matrix. */
export interface CommercialCurrency {
  country_code: string;
  currency: string;
}

interface CommercialCurrencyResponse {
  success?: boolean;
  data?: CommercialCurrency[];
}

// Fields the backend accepts on create/update. We deliberately allowlist these
// so we never leak `applies_to`, `_id`, timestamps, etc. into the payload.
const ALLOWED_FIELDS: (keyof AdminActivationFee)[] = [
  "country_code",
  "currency",
  "hardware_class",
  "fee_minor",
  "tax_profile_id",
  "effective_from",
  "effective_to",
];

const buildPayload = (input: Partial<AdminActivationFee>): Partial<AdminActivationFee> => {
  const out: Partial<AdminActivationFee> = {};
  for (const key of ALLOWED_FIELDS) {
    const value = input[key];
    if (value === undefined) continue;
    // Keep explicit nulls for nullable fields (tax_profile_id, effective_to) so
    // the backend can clear them; drop empty strings for those instead.
    if (value === "" && (key === "tax_profile_id" || key === "effective_to")) {
      (out as Record<string, unknown>)[key] = null;
      continue;
    }
    (out as Record<string, unknown>)[key] = value;
  }
  return out;
};

const unwrapOne = (res: { data?: AdminActivationFeeResponse }): AdminActivationFee | null => {
  const d = res.data?.data;
  return d && !Array.isArray(d) ? d : null;
};

export const getAdminActivationFees = async (
  filters: ActivationFeeFilters = {}
): Promise<AdminActivationFee[]> => {
  const params = new URLSearchParams();
  if (filters.country_code) params.set("country_code", filters.country_code);
  if (filters.hardware_class) params.set("hardware_class", filters.hardware_class);
  const qs = params.toString();
  const url = qs ? `${ENDPOINTS.ADMIN.ACTIVATION_FEES}?${qs}` : ENDPOINTS.ADMIN.ACTIVATION_FEES;
  const res = await apiClient.get<AdminActivationFeeResponse>(url);
  const d = res.data?.data;
  return Array.isArray(d) ? d : [];
};

export const getAdminActivationFee = async (id: string): Promise<AdminActivationFee | null> => {
  const res = await apiClient.get<AdminActivationFeeResponse>(ENDPOINTS.ADMIN.ACTIVATION_FEE_DETAIL(id));
  return unwrapOne(res);
};

export const createAdminActivationFee = async (
  input: Partial<AdminActivationFee>
): Promise<AdminActivationFee | null> => {
  const res = await apiClient.post<AdminActivationFeeResponse>(
    ENDPOINTS.ADMIN.ACTIVATION_FEES,
    buildPayload(input)
  );
  return unwrapOne(res);
};

export const updateAdminActivationFee = async (
  id: string,
  input: Partial<AdminActivationFee>
): Promise<AdminActivationFee | null> => {
  const res = await apiClient.put<AdminActivationFeeResponse>(
    ENDPOINTS.ADMIN.ACTIVATION_FEE_DETAIL(id),
    buildPayload(input)
  );
  return unwrapOne(res);
};

export const deleteAdminActivationFee = async (id: string): Promise<boolean> => {
  const res = await apiClient.delete<AdminActivationFeeResponse>(
    ENDPOINTS.ADMIN.ACTIVATION_FEE_DETAIL(id)
  );
  return Boolean(res.data?.success);
};

export const getCommercialCurrencies = async (): Promise<CommercialCurrency[]> => {
  const res = await apiClient.get<CommercialCurrencyResponse>(ENDPOINTS.ADMIN.COMMERCIAL_CURRENCIES);
  const d = res.data?.data;
  return Array.isArray(d) ? d : [];
};
